import { useState } from 'react'

import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { BiddingBlockApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import { BiddingApprovalState } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  HStack,
  PrimaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { AlertActions } from '~/components/common/AlertActions'
import { AlertDismissButton } from '~/components/common/AlertDismissButton'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import type { ApprovalBannerEntry } from '~/pages/SpotBidding/components/ApprovalBannerStack'
import { ApprovalBannerStack } from '~/pages/SpotBidding/components/ApprovalBannerStack'
import { ApprovalDecisionActions } from '~/pages/SpotBidding/components/ApprovalDecisionActions'
import { ApproverIdentityNotice } from '~/pages/SpotBidding/components/ApproverIdentityNotice'
import { useApproverIdentity } from '~/pages/SpotBidding/hooks/useApproverIdentity'
import type {
  ApprovalBannerHeading,
  ApprovedBannerHeading,
} from '~/pages/SpotBidding/models/approvalBannerHeading'
import {
  APPROVAL_BANNER_ALERT_STATUS,
  APPROVAL_BANNER_TITLE_KEY,
  isApprovedBannerHeading,
  resolveApprovalBannerHeading,
} from '~/pages/SpotBidding/models/approvalBannerHeading'
import type { ApproverIdentityResolver } from '~/pages/SpotBidding/models/approverIdentity'
import { toMatchOutcome } from '~/pages/SpotBidding/models/blockBidStatusCategory'
import { isExpiredBlockGroup } from '~/pages/SpotBidding/models/blockDraftGroup'
import type { SpotMarketPhase } from '~/pages/SpotBidding/models/marketPhase'
import { spotAcceptanceOpenAtLabel } from '~/pages/SpotBidding/models/marketPhase'

/** 承認軸 SUBMITTED のグループを見出しごとにまとめる時の並び (対応が要る状態を先に出す)。 */
const SUBMITTED_AGGREGATE_ORDER: readonly ApprovedBannerHeading[] = [
  'approvedUnconfirmed',
  'approvedSending',
  'approvedReserved',
  'active',
]

type Props = {
  /** 画面の市場フェーズ。 承認済の見出しが入札締切の前後で分かれる (AC-BSP-067)。 */
  phase: SpotMarketPhase
  /** 受渡日。 予約入札フェーズの見出しに受付開始日時を出す。 */
  targetDate: Date
  /**
   * 受渡日のブロック承認依頼 (group) 一覧。 呼び出し元 (index.tsx) が並列 Get 済みの
   * full entity (blockApprovalRequests) をそのまま渡し、 状態別の出し分けは本 component
   * が行う (= 通常入札の `ApprovalStatusBanner` と同型)。
   *
   * 罠: 一覧 (list) 由来の item ではなく明細 Get 済みの entity を渡す。 バナーの操作は
   * `updatedAt` を楽観ロック基準値に使うため、 Get 未解決の group を混ぜると stale な
   * 値で状態遷移を投げてしまう。
   */
  groups: BiddingBlockApprovalRequest[]
  /**
   * PENDING の group に「申請取消」ボタンを出し、 押下時に対象 group を渡す
   * (AC-BSP-055、 = 申請者が下書きに戻す自己リカバリ経路。 取消単位は BlockBidGroup
   * 単位、 通常入札の ApprovalStatusBanner が request 単位なのと対照)。 省略時は
   * 取消ボタンを描画しない (= read-only 表示)。 実 CANCEL RPC・確認 modal は呼び出し元
   * (ページ) の責務。
   */
  onCancelRequest?: (group: BiddingBlockApprovalRequest) => void
  /**
   * REJECTED の group に「再編集して再申請」ボタンを出し、 押下時に対象 group を渡す
   * (AC-BSP-056、 = 差戻し後に申請者が下書きに戻して再編集する経路。 適用範囲は
   * 通常入札・ブロック入札の両方)。 省略時は再編集ボタンを描画しない (= read-only
   * 表示)。 実 REOPEN RPC は呼び出し元 (ページ) の責務で、 確認 modal は挟まない
   * (= 下書きに戻すだけの可逆操作。 design `spot.html` の通常入札 `#bsc-reedit` と同じ
   * 判断)。 締切検証は backend も持つ (AC-BSP-056c)。
   */
  onReopenRequest?: (group: BiddingBlockApprovalRequest) => void
  /**
   * PENDING の group に「承認」ボタンを出し、 押下時に対象 group を渡す
   * (AC-BSP-094 = 承認者の承認経路。 承認案件一覧画面が Phase 1 時点で動作保証
   * 対象外のため、 本画面が承認操作の唯一の経路)。 省略時は描画しない。
   * 実 APPROVE RPC・確認 modal は呼び出し元 (ページ) の責務。
   *
   * 承認者でない人・申請者本人にも押せるまま出し、 近くに注意を出す (AC-BSP-098)。
   */
  onApproveRequest?: (group: BiddingBlockApprovalRequest) => void
  /**
   * PENDING の group に「差戻し」ボタンを出し、 押下時に対象 group を渡す
   * (AC-BSP-094)。 省略時は描画しない。 差戻し理由の入力 (必須、 AC-BSP-063) と
   * 実 REJECT RPC は呼び出し元 (ページ) の責務。
   */
  onRejectRequest?: (group: BiddingBlockApprovalRequest) => void
  /**
   * 承認 / 差戻し を非活性にする理由 (= hover tooltip)。 undefined なら活性。 出し方の
   * 根拠は `ApprovalDecisionActions` の JSDoc。 値は `useBlockApprovalDecision` の
   * `decisionDisabledReason` (= 確定時ガードと同じ policy) をそのまま渡す。
   */
  decisionDisabledReason?: string
}

/** SUBMITTED 集約バナーの dismiss 状態を localStorage に保持する key の prefix。 PR #6607 の `DismissibleInfoAlert` と揃える (共通 component 化の際に迷わないよう)。 */
const SUBMITTED_DISMISS_STORAGE_PREFIX = 'ebalance-alert-dismissed:'

/** SUBMITTED 集約の永続化 key の安定部分 (= 画面 + 用途。 #6607 の `storageKey` 命名と同型)。 */
const SUBMITTED_DISMISS_STORAGE_KEY = 'biddingBlock.approvalBanner.submitted'

/**
 * 件数 + 最新提出日時から dismiss key に混ぜるハッシュを作る (#6607 `hashMessage`
 * と同型の軽いハッシュ。 衝突しても「閉じたはずの集約バナーがもう一度出る」 だけ
 * なので暗号強度は要らない)。
 */
function hashSubmittedAggregate(
  count: number,
  latestSubmittedAt: Date | undefined,
): string {
  const raw = `${count}:${latestSubmittedAt?.toISOString() ?? ''}`
  let hash = 0
  for (let i = 0; i < raw.length; i += 1) {
    hash = (Math.imul(31, hash) + raw.charCodeAt(i)) | 0
  }
  return Math.abs(hash).toString(36)
}

/** 件数・最新提出日時が変われば別 key になる (= 新規提出があれば再表示される)。 */
function buildSubmittedDismissKey(
  count: number,
  latestSubmittedAt: Date | undefined,
): string {
  const hash = hashSubmittedAggregate(count, latestSubmittedAt)
  return `${SUBMITTED_DISMISS_STORAGE_PREFIX}${SUBMITTED_DISMISS_STORAGE_KEY}:h${hash}`
}

/** localStorage が使えない環境 (Safari private 等) では表示側に倒す。 */
function readSubmittedDismissed(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

/** SUBMITTED groups から最新の submittedAt を求める (無ければ undefined)。 */
function latestSubmittedAtOf(
  submittedGroups: readonly BiddingBlockApprovalRequest[],
): Date | undefined {
  return submittedGroups.reduce<Date | undefined>((latest, group) => {
    if (group.submittedAt === undefined) return latest
    const date = timestampDate(group.submittedAt)
    return latest === undefined || date.getTime() > latest.getTime()
      ? date
      : latest
  }, undefined)
}

/**
 * 集約した SUBMITTED groups のうち、 最後に承認された group (承認日時が最新のもの。 承認を経ない
 * 提出しか無ければ undefined)。 集約バナーの承認者・承認日時・申請日時は、 提出日時を最新で出す
 * のと同じく、 この group の値を 3 つそろえて出す。
 */
function latestApprovedGroupOf(
  submittedGroups: readonly BiddingBlockApprovalRequest[],
): BiddingBlockApprovalRequest | undefined {
  return submittedGroups.reduce<BiddingBlockApprovalRequest | undefined>(
    (latest, group) => {
      if (group.approvedAt === undefined) return latest
      if (latest?.approvedAt === undefined) return group
      return timestampDate(group.approvedAt).getTime() >
        timestampDate(latest.approvedAt).getTime()
        ? group
        : latest
    },
    undefined,
  )
}

/**
 * ブロック入札の承認状態バナー。
 *
 * design `spot.html` `#block-approval-status` の `#tab-block` 直下 (Status Hub) 配置に
 * 対応し、 呼び出し元 (index.tsx) がブロック入札タブの内容として描画する。 通常入札の
 * `ApprovalStatusBanner` と異なり、 ブロックは group (承認依頼) 単位で承認状態を持つ
 * (AC-BSP-059: グループ内の各レグは同じ approval_status を投影する)。
 *
 * 表示する状態 (見出しの写像は [[resolveApprovalBannerHeading]]、 AC-BSP-067):
 *   - PENDING: 申請者 / 申請日時。 差戻しから再申請した group (rejected_* 保持) は
 *     前回差戻しコンテキストも併記 (AC-BSP-056)。 申請取消導線 (AC-BSP-055)
 *   - APPROVED: 承認者 / 承認日時 / 申請日時 (AC-BSP-067)
 *   - REJECTED: 差戻し理由 / 差戻し日時 + 再編集導線 (AC-BSP-056)
 *   - SUBMITTED: 送信軸で入札中 / 承認済 (送信中・予約入札・送信を確認できない) に分け、
 *     件数 + 最新提出日時 (承認を経た group があれば承認者・承認日時・申請日時も) の
 *     **見出しごとの集約バナー** (AC-BSP-067、 下記「SUBMITTED の集約」参照)
 *   - DRAFT (REOPEN 後で rejected_* 保持): 前回差戻しコンテキスト (AC-BSP-056)
 *
 * **ブロックは 1 グループ = 1 案件** で、 通常入札のようにラウンド単位で束ねられない
 * (CSV 取込は 1 リクエスト 2,500 グループまで。 `spot-bid-block-csv-import.md` §5、 かつ同 spec が
 * 「CSV 取込のグループは常に新規の下書き申請セットとして作成する」 と確定している)。
 * 素直に全件縦積みすると画面が丸ごと埋まるため、 通常入札と同じ `ApprovalBannerStack`
 * で 3 件までを並べ、 それを超えた分をチップに畳む (2026-08-26 デザイナー判断)。
 *
 * UNSPECIFIED / FAILED / CANCELLED と差戻し履歴の無い純粋な DRAFT、 承認なし・自動の
 * エリアの group は非表示 (= null)。 承認状態バナーの見出しは AC-BSP-067
 * (Ubiquitous = 通常・ブロック共通) が SoT で、 通常入札の `ApprovalStatusBanner` と
 * 同じ写像に揃える (spec `spot-bidding.md` §9 の画面文言 SoT が「入札中」を正としており、
 * 「提出済」は旧表記として排されている。 `docs/domain-terms.md`「入札中」項 issue #3230)。
 *
 * ### SUBMITTED の集約 (issue #6552 レビュー指摘)
 *
 * SUBMITTED は**終端状態**で、 提出後は当日ずっとその group が SUBMITTED のまま
 * 残る。 ブロックは 1 グループ = 1 案件 (上記) で通常入札のようにラウンド単位に
 * 束ねられないため、 SUBMITTED の group を個別 entry のまま積むと JEPX 提出後は
 * `ApprovalBannerStack` の VISIBLE_LIMIT (3 件) が SUBMITTED バナーで埋まり
 * 続け、 承認待ち・差戻し等の「次に手を動かすべきもの」 が畳まれてしまう。
 * design `spot.html` の `renderBlockApprovalStatus` もブロックタブには常に
 * 「バー 1 枚 + `件数 N 件`」 の集約表示しか持たない (状態ごとに複数枚を縦積みする
 * 作りではない)。 そのため SUBMITTED の group は個別 entry 化せず、 件数 + 最新
 * 提出日時の集約バナーにまとめる。 送信軸が違う group を同じ見出しにまとめると
 * 未送信の group に「JEPX へ送信済みです。」 と出てしまうため、 集約は見出しごとに
 * 1 枚とする。 JEPX が受け付けた (入札中) の集約だけ閉じる (×) を付けて当日ずっと
 * 居座らないようにした。 APPROVED も集約対象にするかは Des 判断待ち (**issue #6640**)。
 *
 * 最終承認は同じ呼び出しで提出まで進む (AC-BSP-094) ので、 承認直後の group も SUBMITTED の
 * 集約に入る。 AC-BSP-067 は承認済・入札中のどちらも申請→承認の日時と承認者を表示すると
 * 定めるため、 集約には承認を経た group のうち最後に承認されたものの承認者・承認日時・申請日時を
 * 出す (提出日時を最新で出すのと同じ扱い。 承認を経ない提出しか無い集約には出さない)。
 *
 * 閉じた状態は localStorage に保持し、 件数か最新提出日時が変わったら (= 新規提出
 * が起きたら) 再表示する。 契約は `Supply48Data` の `DismissibleInfoAlert`
 * (PR #6607、 本 PR 時点で未マージのため import せず、 同じ localStorage key
 * prefix (`ebalance-alert-dismissed:`) / aria-label / throw 時は表示側に倒す、 の
 * 3 点だけ独立に揃えた) と同型。 #6607 マージ後、 共通 component への寄せ替えが
 * 妥当か検討する。
 */
export const BlockApprovalStatusBanner = ({
  phase,
  targetDate,
  groups,
  onCancelRequest,
  onReopenRequest,
  onApproveRequest,
  onRejectRequest,
  decisionDisabledReason,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const resolveDisplayName = useMemberDisplayName()
  const approverIdentity = useApproverIdentity()

  const context = { phase, targetDate }
  // 承認軸 SUBMITTED のグループは見出しごとに 1 枚へ集約する (doc コメント「SUBMITTED の集約」)。
  // 送信軸が違うグループ (送信済み・送信中 等) を 1 枚にまとめると、 未送信の
  // グループに「JEPX へ送信済みです。」 と出てしまう。
  const submittedGroupsByHeading = new Map<
    ApprovedBannerHeading,
    BiddingBlockApprovalRequest[]
  >()
  for (const group of groups) {
    if (group.approvalState !== BiddingApprovalState.SUBMITTED) continue
    const heading = resolveGroupHeading(group, context)
    if (heading === undefined || !isApprovedBannerHeading(heading)) continue
    submittedGroupsByHeading.set(heading, [
      ...(submittedGroupsByHeading.get(heading) ?? []),
      group,
    ])
  }
  // 「閉じる」 を付けるのは JEPX が受け付けた (入札中) の集約だけ。 送信できていない・送信を
  // 確認できていない集約は、 対応が済むまで閉じさせない。
  const submittedGroups = submittedGroupsByHeading.get('active') ?? []
  const submittedCount = submittedGroups.length
  const latestSubmittedAt = latestSubmittedAtOf(submittedGroups)
  const submittedDismissKey = buildSubmittedDismissKey(
    submittedCount,
    latestSubmittedAt,
  )

  // 「閉じた」の永続 SoT は localStorage。 `manuallyDismissedKey` はこのセッション
  // 中に押下した dismiss を即座に反映するための楽観 state で、 現在の
  // `submittedDismissKey` と一致する時だけ有効にする (= 件数・最新提出日時が
  // 変わって key が変わったら、 古い dismiss は効かなくなり localStorage の値
  // (無ければ false = 表示) に戻る。 新規提出のたびに再マウントさせる仕掛けを
  // 作らずに「内容が変われば再表示」 を満たせる)。
  const [manuallyDismissedKey, setManuallyDismissedKey] = useState<
    string | null
  >(null)
  const isSubmittedDismissed =
    manuallyDismissedKey === submittedDismissKey ||
    readSubmittedDismissed(submittedDismissKey)

  const handleDismissSubmitted = (): void => {
    setManuallyDismissedKey(submittedDismissKey)
    try {
      window.localStorage.setItem(submittedDismissKey, '1')
    } catch {
      // 永続化だけ諦める (このセッション中は manuallyDismissedKey で閉じたままになる)。
    }
  }

  // group と banner の対応を保ったまま渡す。 並べ替え・畳み込みは共通部品の責務
  // (通常入札の `ApprovalStatusBanner` と同じ見せ方に揃える)。 SUBMITTED は個別
  // entry 化せず、 下で見出しごとの集約バナーにまとめる (doc コメント「SUBMITTED の集約」)。
  // バナー要素は ApprovalBannerStack が表示する分だけ作る (render 関数を渡す)。 バナーを
  // 出さない group はここで除く (= 3 件の枠や「残り N 件」 の数に含めない)。
  // 出すかの判断と見出しは 1 回だけ決め、 render へはその結果を渡す。
  const entries: ApprovalBannerEntry[] = groups.flatMap((group) => {
    if (group.approvalState === BiddingApprovalState.SUBMITTED) {
      return []
    }
    const kind = bannerKindOf(group, context)
    if (kind === undefined) {
      return []
    }
    return [
      {
        id: group.id,
        approvalState: group.approvalState,
        render: () =>
          renderBannerForGroup(
            group,
            kind,
            t,
            {
              onCancelRequest,
              onReopenRequest,
              onApproveRequest,
              onRejectRequest,
              decisionDisabledReason,
              resolveDisplayName,
              approverIdentity,
            },
            context,
          ),
      },
    ]
  })

  for (const heading of SUBMITTED_AGGREGATE_ORDER) {
    const aggregated = submittedGroupsByHeading.get(heading)
    if (aggregated === undefined) continue
    const isDismissible = heading === 'active'
    if (isDismissible && isSubmittedDismissed) continue
    entries.push({
      id: `${heading}-aggregate`,
      approvalState: BiddingApprovalState.SUBMITTED,
      render: () =>
        renderSubmittedAggregateBanner({
          heading,
          count: aggregated.length,
          latestSubmittedAt: latestSubmittedAtOf(aggregated),
          approval: approvalOfAggregate(aggregated, resolveDisplayName),
          targetDate,
          t,
          onDismiss: isDismissible ? handleDismissSubmitted : undefined,
        }),
    })
  }

  return <ApprovalBannerStack entries={entries} />
}

/** バナー上の操作導線 (= 省略された導線はボタンを描画しない)。 */
type BannerHandlers = Pick<
  Props,
  | 'onCancelRequest'
  | 'onReopenRequest'
  | 'onApproveRequest'
  | 'onRejectRequest'
  | 'decisionDisabledReason'
> & {
  resolveDisplayName: (memberId: string | undefined) => string | undefined
  approverIdentity: ApproverIdentityResolver
}

/** 集約バナーに出す承認の記録 (承認を経た group の承認者・承認日時・申請日時)。 */
type AggregateApproval = {
  approvedBy: string | undefined
  approvedAt: Date | undefined
  requestedAt: Date | undefined
}

/**
 * 集約した SUBMITTED groups の承認の記録。 AC-BSP-067 は承認済・入札中のどの見出しでも
 * 申請→承認の日時と承認者を表示すると定める。 承認を経ない提出しか無い集約は undefined。
 * 承認者は member ID でなく表示名にし、 解決できなければ「—」 (承認済の個別バナーと同じ)。
 */
function approvalOfAggregate(
  aggregated: readonly BiddingBlockApprovalRequest[],
  resolveDisplayName: (memberId: string | undefined) => string | undefined,
): AggregateApproval | undefined {
  const group = latestApprovedGroupOf(aggregated)
  if (group === undefined) return undefined
  return {
    approvedBy:
      group.approvedBy === undefined
        ? undefined
        : (resolveDisplayName(group.approvedBy) ?? '—'),
    approvedAt:
      group.approvedAt === undefined
        ? undefined
        : timestampDate(group.approvedAt),
    requestedAt:
      group.requestedAt === undefined
        ? undefined
        : timestampDate(group.requestedAt),
  }
}

/**
 * SUBMITTED の group を見出しごとに件数 + 最新提出日時 1 枚に集約したバナー
 * (doc コメント「SUBMITTED の集約」参照)。 承認を経た group があれば、 承認者・承認日時・
 * 申請日時も出す (AC-BSP-067)。 閉じる (×) は `onDismiss` がある時だけ出し、
 * 呼び出し元 (`BlockApprovalStatusBanner`) が管理する dismiss state を叩くだけで、 本関数
 * 自体は hook を持たない (= ファイル内の他の render* 関数と同型の plain 関数)。
 */
function renderSubmittedAggregateBanner({
  heading,
  count,
  latestSubmittedAt,
  approval,
  targetDate,
  t,
  onDismiss,
}: {
  heading: ApprovedBannerHeading
  count: number
  latestSubmittedAt: Date | undefined
  approval: AggregateApproval | undefined
  targetDate: Date
  t: TFunction
  onDismiss: (() => void) | undefined
}): React.ReactElement {
  return (
    <Alert
      key={`${heading}-aggregate`}
      description={
        <HStack flexWrap="wrap" gap={4}>
          {approval?.approvedBy !== undefined && (
            <Text>
              {t('page:biddingBlock.approvalBanner.approvedBy')}:{' '}
              {approval.approvedBy}
            </Text>
          )}
          {approval?.approvedAt !== undefined && (
            <Text>
              {t('page:biddingBlock.approvalBanner.approvedAt')}:{' '}
              {approval.approvedAt.toLocaleString('ja-JP', {
                timeZone: 'Asia/Tokyo',
              })}
            </Text>
          )}
          {approval?.requestedAt !== undefined && (
            <Text>
              {t('page:biddingBlock.approvalBanner.requestedAt')}:{' '}
              {approval.requestedAt.toLocaleString('ja-JP', {
                timeZone: 'Asia/Tokyo',
              })}
            </Text>
          )}
          <Text>
            {t('page:biddingBlock.approvalBanner.submittedGroupCount')}:{' '}
            {t('page:biddingBlock.approvalBanner.submittedGroupCountValue', {
              count,
            })}
          </Text>
          {latestSubmittedAt !== undefined && (
            <Text>
              {t('page:biddingBlock.approvalBanner.submittedAt')}:{' '}
              {latestSubmittedAt.toLocaleString('ja-JP', {
                timeZone: 'Asia/Tokyo',
              })}
            </Text>
          )}
        </HStack>
      }
      status={APPROVAL_BANNER_ALERT_STATUS[heading]}
      title={t(APPROVAL_BANNER_TITLE_KEY[heading], {
        openAt: spotAcceptanceOpenAtLabel(targetDate),
      })}
    >
      {/* 閉じる (×) は共通の AlertDismissButton (proto `.ecl-alert-dismiss` =
          28px・aria-label / Tooltip「閉じる」)。 DismissibleInfoAlert と同じ部品。 */}
      {onDismiss !== undefined && <AlertDismissButton onClick={onDismiss} />}
    </Alert>
  )
}

/** バナーの種類。 見出し (AC-BSP-067) か、 差戻し後に再編集した下書きのバナー。 */
type BannerKind = ApprovalBannerHeading | 'reopenedDraft'

/**
 * group のバナーの種類を決める。 undefined はバナーを出さない (= 3 件の枠や「残り N 件」 に
 * 数えない)。 差戻し履歴の無い純粋な下書きと、 見出しを決められない依頼は出さない。
 * 入札締切を迎えて失効にした下書き (AC-BSP-099) も出さない: 差戻しの理由・日時が札に
 * 残っていても、 再編集して再申請する下書きではないので「前回の差戻し内容 (再編集中)」 とは言わない。
 */
function bannerKindOf(
  group: BiddingBlockApprovalRequest,
  context: Pick<Props, 'phase' | 'targetDate'>,
): BannerKind | undefined {
  if (group.approvalState === BiddingApprovalState.DRAFT) {
    if (isExpiredBlockGroup(group)) {
      return undefined
    }
    const hasPriorRejection =
      group.rejectedReason !== undefined || group.rejectedAt !== undefined
    return hasPriorRejection ? 'reopenedDraft' : undefined
  }
  return resolveGroupHeading(group, context)
}

/** グループの承認依頼から見出しを決める。 バナー対象外は undefined。 */
function resolveGroupHeading(
  group: BiddingBlockApprovalRequest,
  context: Pick<Props, 'phase' | 'targetDate'>,
): ApprovalBannerHeading | undefined {
  return resolveApprovalBannerHeading({
    approvalMode: group.approvalMode,
    approvalState: group.approvalState,
    sendStatus: group.sendStatus,
    phase: context.phase,
    matchOutcomes: [group.primary, group.secondary]
      .filter((leg) => leg !== undefined)
      .map((leg) => toMatchOutcome(leg.bidStatus)),
  })
}

/**
 * 1 件の承認依頼 (group) から、 `bannerKindOf` が決めた種類の承認状態バナーを組み立てる。
 * SUBMITTED は呼び出し元 (`BlockApprovalStatusBanner`) が集約バナーとして別途
 * 扱うため、 本関数には渡ってこない。
 */
function renderBannerForGroup(
  group: BiddingBlockApprovalRequest,
  kind: BannerKind,
  t: TFunction,
  handlers: BannerHandlers,
  context: Pick<Props, 'phase' | 'targetDate'>,
): React.ReactElement {
  const {
    onCancelRequest,
    onReopenRequest,
    onApproveRequest,
    onRejectRequest,
    decisionDisabledReason,
    resolveDisplayName,
    approverIdentity,
  } = handlers

  // DRAFT: 差戻し後に再編集 (REOPEN) した下書きは rejected_* が保持されており
  // (AC-BSP-056: 差戻し理由・日時は表示保持)、 修正作業中に差戻し理由を参照できる
  // よう前回差戻しコンテキストを表示する。 差戻し履歴の無い純粋な下書きは
  // メインアクションボタン側で状態が分かるためバナー不要 (= `bannerKindOf` が除く)。
  if (kind === 'reopenedDraft') {
    return renderReopenedDraftBanner(group, t)
  }
  const heading = kind

  if (heading === 'pending') {
    const requestedAtDate =
      group.requestedAt !== undefined
        ? timestampDate(group.requestedAt)
        : undefined
    return (
      // 承認申請中は warning (= 人の承認待ちで止まっている状態。
      // design `spot.html` の `#block-appr-bar` も pending は `ecl-alert--warning`)。
      // 通常入札の ApprovalStatusBanner の PENDING と揃える。
      <Alert
        key={group.id}
        description={
          <>
            <HStack flexWrap="wrap" gap={4}>
              {group.requestedBy !== undefined && (
                <Text>
                  {t('page:biddingBlock.approvalBanner.requestedBy')}:{' '}
                  {resolveDisplayName(group.requestedBy) ?? '—'}
                </Text>
              )}
              {requestedAtDate !== undefined && (
                <Text>
                  {t('page:biddingBlock.approvalBanner.requestedAt')}:{' '}
                  {requestedAtDate.toLocaleString('ja-JP', {
                    timeZone: 'Asia/Tokyo',
                  })}
                </Text>
              )}
            </HStack>
            {(onApproveRequest !== undefined ||
              onRejectRequest !== undefined) && (
              <ApproverIdentityNotice identity={approverIdentity(group)} />
            )}
            {/*
             * 差戻し後に再申請した PENDING は rejected_* を保持する (backend の
             * RequestApproval は rejected_* を消さない)。 承認者が前回差戻しを
             * 把握できるよう、 前回差戻しコンテキスト (理由・日時) を併記する
             * (AC-BSP-056、 design `spot.html` の `isReapply` 表示と同型)。
             */}
            {renderPriorRejectionSection(group, t)}
          </>
        }
        status={APPROVAL_BANNER_ALERT_STATUS.pending}
        title={t(APPROVAL_BANNER_TITLE_KEY.pending)}
      >
        {/* 右端の操作群 (申請取消 / 差戻し / 承認)。 通常入札の ApprovalStatusBanner と
            同じ共通部品で、 並び・variant・無効化の見せ方は ApprovalDecisionActions の
            JSDoc。 承認単位は BlockBidGroup (AC-BSP-059)。 */}
        <AlertActions>
          <ApprovalDecisionActions
            decisionDisabledReason={decisionDisabledReason}
            labels={{
              cancel: t('page:biddingBlock.action.cancel'),
              reject: t('page:biddingBlock.action.reject'),
              approve: t('page:biddingBlock.action.approve'),
            }}
            onApprove={
              onApproveRequest === undefined
                ? undefined
                : () => {
                    onApproveRequest(group)
                  }
            }
            onCancel={
              onCancelRequest === undefined
                ? undefined
                : () => {
                    onCancelRequest(group)
                  }
            }
            onReject={
              onRejectRequest === undefined
                ? undefined
                : () => {
                    onRejectRequest(group)
                  }
            }
          />
        </AlertActions>
      </Alert>
    )
  }

  if (heading === 'rejected') {
    const rejectedAtDate =
      group.rejectedAt !== undefined
        ? timestampDate(group.rejectedAt)
        : undefined
    return (
      <Alert
        key={group.id}
        description={
          group.rejectedReason !== undefined || rejectedAtDate !== undefined ? (
            <VStack align="stretch" gap={1}>
              {group.rejectedReason !== undefined && (
                <Text>
                  {t('page:biddingBlock.approvalBanner.rejectedReason')}:{' '}
                  {group.rejectedReason}
                </Text>
              )}
              {rejectedAtDate !== undefined && (
                <Text>
                  {t('page:biddingBlock.approvalBanner.rejectedAt')}:{' '}
                  {rejectedAtDate.toLocaleString('ja-JP', {
                    timeZone: 'Asia/Tokyo',
                  })}
                </Text>
              )}
            </VStack>
          ) : undefined
        }
        status={APPROVAL_BANNER_ALERT_STATUS.rejected}
        title={t(APPROVAL_BANNER_TITLE_KEY.rejected)}
      >
        {onReopenRequest !== undefined && (
          <AlertActions>
            {/*
             * AC-BSP-056: 差戻し後に申請者が下書きに戻して再編集する経路。
             * 差し戻されたブロックは再申請しない限り前に進まないため、 差戻し
             * バナー上ではこれが主操作 = PrimaryButton (通常入札の
             * ApprovalStatusBanner と同じ判断、 2026-08-17 デザイナー指摘 / #5147)。
             * 押下で呼び出し元 (ページ) が即 REOPEN する (= 確認 modal は挟まない)。
             */}
            <PrimaryButton
              onClick={() => {
                onReopenRequest(group)
              }}
              size="sm"
            >
              {t('page:biddingBlock.action.reopen')}
            </PrimaryButton>
          </AlertActions>
        )}
      </Alert>
    )
  }

  // 承認済 (承認軸の APPROVED を送信軸で分けた見出し)。
  const approvedAtDate =
    group.approvedAt !== undefined ? timestampDate(group.approvedAt) : undefined
  const approvedRequestedAtDate =
    group.requestedAt !== undefined
      ? timestampDate(group.requestedAt)
      : undefined
  return (
    <Alert
      key={group.id}
      description={
        <HStack flexWrap="wrap" gap={4}>
          {group.approvedBy !== undefined && (
            <Text>
              {t('page:biddingBlock.approvalBanner.approvedBy')}:{' '}
              {resolveDisplayName(group.approvedBy) ?? '—'}
            </Text>
          )}
          {approvedAtDate !== undefined && (
            <Text>
              {t('page:biddingBlock.approvalBanner.approvedAt')}:{' '}
              {approvedAtDate.toLocaleString('ja-JP', {
                timeZone: 'Asia/Tokyo',
              })}
            </Text>
          )}
          {/* 承認済でも「いつ申請したか」 が要る (通常入札の ApprovalStatusBanner
              と同じ判断)。 申請日時は承認後も残る (backend は approve で
              requested_* を消さない)。 */}
          {approvedRequestedAtDate !== undefined && (
            <Text>
              {t('page:biddingBlock.approvalBanner.requestedAt')}:{' '}
              {approvedRequestedAtDate.toLocaleString('ja-JP', {
                timeZone: 'Asia/Tokyo',
              })}
            </Text>
          )}
        </HStack>
      }
      status={APPROVAL_BANNER_ALERT_STATUS[heading]}
      title={t(APPROVAL_BANNER_TITLE_KEY[heading], {
        openAt: spotAcceptanceOpenAtLabel(context.targetDate),
      })}
    />
  )
}

/**
 * REOPEN 後 (= 差戻し → 下書き) の DRAFT に、 前回差戻しのコンテキスト
 * (差戻し理由・日時) を表示するバナー (AC-BSP-056)。 `bannerKindOf` が選んだ
 * (= rejected_* を保持する) 下書きだけが届く。
 */
function renderReopenedDraftBanner(
  group: BiddingBlockApprovalRequest,
  t: TFunction,
): React.ReactElement {
  const rejectionRows = renderPriorRejectionRows(group, t)
  return (
    <Alert
      key={group.id}
      description={rejectionRows}
      status="warning"
      title={t('page:biddingBlock.approvalBanner.reopenedDraftTitle')}
    />
  )
}

/**
 * 前回差戻しコンテキスト (差戻し理由・日時) の行。 REOPEN 後の DRAFT (再編集中) と、
 * 再申請後の PENDING (再申請中) の双方でバナー内に併記する (AC-BSP-056)。
 * rejected_* を保持しない場合は null。
 */
function renderPriorRejectionRows(
  group: BiddingBlockApprovalRequest,
  t: TFunction,
): React.ReactElement | null {
  const { rejectedReason, rejectedAt } = group
  if (rejectedReason === undefined && rejectedAt === undefined) {
    return null
  }
  const rejectedAtDate =
    rejectedAt !== undefined ? timestampDate(rejectedAt) : undefined
  return (
    <VStack align="stretch" gap={1}>
      {rejectedReason !== undefined && (
        <Text>
          {t('page:biddingBlock.approvalBanner.rejectedReason')}:{' '}
          {rejectedReason}
        </Text>
      )}
      {rejectedAtDate !== undefined && (
        <Text>
          {t('page:biddingBlock.approvalBanner.rejectedAt')}:{' '}
          {rejectedAtDate.toLocaleString('ja-JP', {
            timeZone: 'Asia/Tokyo',
          })}
        </Text>
      )}
    </VStack>
  )
}

/**
 * PENDING バナーに併記する前回差戻しブロック (小見出し + 差戻し行)。 rejected_* を
 * 保持しない純粋な申請中は null (= 併記しない)。 見出しは DRAFT 版とは状態が異なる
 * (再申請中 vs 再編集中) ため priorRejectionTitle を用いる。
 */
function renderPriorRejectionSection(
  group: BiddingBlockApprovalRequest,
  t: TFunction,
): React.ReactElement | null {
  const rejectionRows = renderPriorRejectionRows(group, t)
  if (rejectionRows === null) {
    return null
  }
  return (
    <VStack align="stretch" gap={1} marginTop={2}>
      <Text fontWeight="semibold">
        {t('page:biddingBlock.approvalBanner.priorRejectionTitle')}
      </Text>
      {rejectionRows}
    </VStack>
  )
}
